import { Row, RowItemType, Section, SectionType, SelectorIdentifier, UIElementType } from '../../models';
import { InstallationSeries, InstallationType } from 'app/shared/models/location-details';
import { pipeInstallationConfig } from './installation-types/pipe';
import { weirInstallationConfig } from './installation-types/weir';
import { flumeInstallationConfig } from './installation-types/flume';
import { removeSection, statusChange, attachSectionToRow, tagKeyChange } from '../../actions';
import { FlexSectionIds, Mp2BasicInfoFieldId } from './base-config-models';
import { InstallationTypeSectionIds } from '../common.models';
import { LocationCardState } from '../../reducers';
import { QCONTINUITY_ENTITY, QFLUME_ENTITY, QLOOKUP_ENTITY, QWEIR_ENTITY } from 'app/shared/constant';
import { field } from '../../core/field-schema';
import { required, maxLength } from '../../core/validator-builders';
import { EntityResponseItem } from 'app/shared/models/view-data';
import { LocationCardStatus, RainGaugeLocation } from '../../../models/location-card.model';
import { FlexLocationCardState } from './models';
import { RainLocation } from 'app/shared/models/locations';

export const customPropertyRowGenerator = (index: number, populateValues?: { key: string; value: string }): Row => {
    return {
        info: {
            id: `custom-property-${index}`,
            panelClass: 'custom-50p',
        },
        items: [
            field.autocomplete<string, string>(`${Mp2BasicInfoFieldId.CustomPropertyKey}-${index}`, 'key', {
                displayMapper: (option) => option,
                clearable: false,
                autoClearOnFocus: false,
                valueMapper: (option) => option,
                optionsSelector: SelectorIdentifier.TagKeyAutocomplete,
                value: populateValues ? populateValues.key : null,
                validators: [required(), maxLength({ value: 128 })],
                placeholder: 'ADD_EDIT.INPUT_TAG_NAME',
                panelClass: 'width-90',
                events: {
                    onBlur: (value) => [
                        tagKeyChange({
                            fieldId: `${Mp2BasicInfoFieldId.CustomPropertyKey}-${index}`,
                            newValue: value,
                            isMp1: false,
                        }),
                    ],
                },
            }),
            field.text(`${Mp2BasicInfoFieldId.CustomPropertyValue}-${index}`, 'value', {
                value: populateValues ? populateValues.value : null,
                placeholder: 'ADD_EDIT.INPUT_TAG_VALUE',
                validators: [required(), maxLength({ value: 128 })],
                panelClass: 'width-90',
            }),
        ],
    };
};

/**
 * MP2 basic info form configuration
 */
export const mp2basicInfo = (state: FlexLocationCardState): Section => {
    const settings = state.settings;
    const details = state.locationDetails;
    const mp2Info = details ? details[1] : null;

    const rainGuage =
        mp2Info && mp2Info.assignedRainGaugeLocationId
            ? state.options.rainGauges.data.find((v) => v.locationId === mp2Info.assignedRainGaugeLocationId)
            : null;

    const mp2Config: Section = {
        info: {
            id: FlexSectionIds.Mp2BasicInfo,
            title: 'COMMON.MP_BASIC_INFORMATION',
            displayTitle: true,
            expanded: false,
            expandable: true,
            panelClass: FlexSectionIds.Mp2BasicInfo,
        },
        rows: [
            // Row 1: QFinalEntityID and AssignedRainGauge/Status (based on edit mode)
            {
                info: {
                    id: 'basic-info-row-1',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<EntityResponseItem, number>(Mp2BasicInfoFieldId.QFinalEntityID, 'qFinalEntityID', {
                        value: mp2Info ? mp2Info.qFinalEntityID : QCONTINUITY_ENTITY,
                        panelClass: 'each-Item',
                        placeholder: 'COMMON.QFINALTEXT',
                        optionsSelector: SelectorIdentifier.QFinalEntities,
                        valueMapper: (option) => option.entityId,
                        displayMapper: (option) => option.entityName,
                    }),
                    mp2Info
                        ? {
                              id: Mp2BasicInfoFieldId.StatusSection,
                              type: RowItemType.Section,
                              section: {
                                  info: {
                                      id: 'status-container',
                                      title: '',
                                      displayTitle: false,
                                      expandable: false,
                                      panelClass: 'status-group custom-width',
                                  },
                                  rows: [
                                      {
                                          info: {
                                              id: 'status-row',
                                              panelClass: 'status-inner-row flex align-items-center gap-4',
                                          },
                                          items: [
                                              field.select<LocationCardStatus, number>(Mp2BasicInfoFieldId.Status, 'status', {
                                                  value: mp2Info.status,
                                                  panelClass: 'custom-width',
                                                  label: 'Status',
                                                  valueMapper: (option) => option.key,
                                                  displayMapper: (option) => option.name,
                                                  validators: [required()],
                                                  optionsSelector: SelectorIdentifier.LocationStatuses,
                                                  events: {
                                                      onChange: () => [statusChange({ isMp1: false })],
                                                  },
                                              }),
                                              field.select<number, number>(
                                                  Mp2BasicInfoFieldId.MaintenanceInterval,
                                                  'maintenanceInterval',
                                                  {
                                                      value: 2,
                                                      label: 'CUSTOMER_EDITOR.HOURS',
                                                      valueMapper: (option) => option,
                                                      displayMapper: (option) => String(option),
                                                      panelClass: 'width-15',
                                                      validators: [required()],
                                                      visible: mp2Info.status === 1,
                                                      optionsSelector: SelectorIdentifier.MaintenanceIntervals,
                                                  },
                                              ),
                                              {
                                                  id: Mp2BasicInfoFieldId.MaintenanceEnd,
                                                  name: 'maintenanceEnd',
                                                  panelClass: 'width-25 field-status-info',
                                                  label: 'CUSTOMER_EDITOR.HOURS',
                                                  type: RowItemType.UI,
                                                  uiType: UIElementType.Text,
                                                  visible: mp2Info.status === 1,
                                              },
                                          ],
                                      },
                                  ],
                              },
                          }
                        : field.autocomplete<RainLocation, string>(
                              Mp2BasicInfoFieldId.AssignedRainGauge,
                              'assignedRainGauge',
                              {
                                  value: mp2Info ? mp2Info.assignedRainGaugeLocationName : null,
                                  panelClass: 'each-Item',
                                  placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                                  clearable: true,
                                  optionsSelector: SelectorIdentifier.RainGauges,
                                  displayMapper: (option) => option.locationName,
                                  valueMapper: (option) => option.locationName,
                              },
                          ),
                ],
            },
            // Row 3: Installation Type and Installation Date
            {
                info: {
                    id: 'installation-row-mp2',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<InstallationType, string>(Mp2BasicInfoFieldId.InstallationType, 'installationType', {
                        value: mp2Info ? `${mp2Info.installationType}: ${mp2Info.installationShapeTypeID}` : '',
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.INSTALLATION_TYPE',
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.FlexInstallationTypes,
                        panelClass: 'custom-width',
                        valueMapper: (option) => option.value,
                        displayMapper: (option) => option.text,
                        events: {
                            onChange: (value: InstallationType) => {
                                const toDispatch = [];
                                const installationSections = [
                                    InstallationTypeSectionIds.Pipe,
                                    InstallationTypeSectionIds.Weir,
                                    InstallationTypeSectionIds.Flume,
                                ];

                                // Remove all possible installation sections
                                // (only one should exist at any time)
                                installationSections.forEach((sectionId) => {
                                    toDispatch.push(
                                        removeSection({ sectionId, parentSectionId: FlexSectionIds.Mp2BasicInfo }),
                                    );
                                });
                                // For Pipe type (Series 200)
                                if (value.series === InstallationSeries.Pipe) {
                                    toDispatch.push(
                                        attachSectionToRow({
                                            targetSectionId: FlexSectionIds.Mp2BasicInfo, // The parent section ID
                                            rowId: 'installation-row-mp2',
                                            fieldId: InstallationTypeSectionIds.Pipe,
                                            sectionConfig: pipeInstallationConfig(state, false),
                                        }),
                                    );
                                } else if (value.series === InstallationSeries.Weir) {
                                    toDispatch.push(
                                        attachSectionToRow({
                                            targetSectionId: FlexSectionIds.Mp2BasicInfo,
                                            rowId: 'installation-row-mp2',
                                            fieldId: InstallationTypeSectionIds.Weir,
                                            sectionConfig: weirInstallationConfig(state, false),
                                        }),
                                    );
                                } else if (value.series === InstallationSeries.Flume) {
                                    toDispatch.push(
                                        attachSectionToRow({
                                            targetSectionId: FlexSectionIds.Mp2BasicInfo,
                                            rowId: 'installation-row-mp2',
                                            fieldId: InstallationTypeSectionIds.Flume,
                                            sectionConfig: flumeInstallationConfig(state, false),
                                        }),
                                    );
                                }

                                return toDispatch;
                            },
                        },
                    }),
                    field.date(Mp2BasicInfoFieldId.InstallationDate, 'installationDate', {
                        panelClass: 'each-Item',
                        placeholder: 'Installation Date',
                        dateFormat: settings.dateFormat,
                        datePickerType: 'date',
                        timeInterval: 1,
                        value: mp2Info ? mp2Info.installationDate : new Date(),
                        events: {
                            // onChange: (value) => [
                            //     { type: FormActionType.INSTALLATION_DATE_CHANGED, payload: { value } }
                            // ]
                        },
                    }),
                ],
            },

            // Row for assignedRainGauge in edit mode
            {
                info: {
                    id: 'edit-mode-rain-gauge-row',
                },
                items: mp2Info
                    ? [
                          field.autocomplete<RainLocation, string>(
                              Mp2BasicInfoFieldId.AssignedRainGauge,
                              'assignedRainGauge',
                              {
                                  value: rainGuage ? rainGuage.locationName : null,
                                  autoClearOnFocus: true,
                                  panelClass: 'each-Item',
                                  placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_ASSIGNED',
                                  clearable: true,
                                  optionsSelector: SelectorIdentifier.RainGaugeAutocomplete,
                                  displayMapper: (option) => option.locationName,
                                  valueMapper: (option) => option.locationName,
                              },
                          ),
                      ]
                    : [],
            },

            // Custom properties section (tags)
            {
                info: {
                    id: 'custom-properties-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: Mp2BasicInfoFieldId.CustomPropertiesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: Mp2BasicInfoFieldId.CustomPropertiesSection,
                                title: 'ADD_EDIT.TAGS_SECTION_NAME',
                                displayTitle: false,
                                displayActionBtns: true,
                                type: SectionType.Array,
                                expanded: false,
                                expandable: false,
                                itemGenerator: customPropertyRowGenerator,
                                panelClass: 'custom-properties-panel',
                            },
                            rows:
                                mp2Info && mp2Info.customProperties
                                    ? mp2Info.customProperties.map((item, index) =>
                                          customPropertyRowGenerator(index, item),
                                      )
                                    : [],
                        },
                    },
                ],
            },
        ],
    };

    if (!mp2Info) {
        return mp2Config;
    }

    const installationConfig = pickInstallationTypeConfig(mp2Info.installationShapeTypeID)(state, false);
    const sectionId = getInstallationTypeSectionId(mp2Info.installationShapeTypeID);

    const installationTypeRowIndex = mp2Config.rows.findIndex((row) => row.info.id === Mp2BasicInfoFieldId.InstallationRow);
    mp2Config.rows.splice(installationTypeRowIndex + 1, 0, {
        info: { id: sectionId, panelClass: 'full-width' },
        items: [{ id: sectionId, type: RowItemType.Section, section: installationConfig }],
    });

    return mp2Config;
};

function pickInstallationTypeConfig(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return pipeInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return weirInstallationConfig;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Rain) {
        return flumeInstallationConfig;
    }
}

function getInstallationTypeSectionId(installationSeries: number) {
    if (installationSeries >= InstallationSeries.Pipe && installationSeries < InstallationSeries.Weir) {
        return InstallationTypeSectionIds.Pipe;
    } else if (installationSeries >= InstallationSeries.Weir && installationSeries < InstallationSeries.Flume) {
        return InstallationTypeSectionIds.Weir;
    } else if (installationSeries >= InstallationSeries.Flume && installationSeries < InstallationSeries.Rain) {
        return InstallationTypeSectionIds.Flume;
    }
}
